import React from 'react';
import { Section, FadeIn, StaggerContainer, StaggerItem } from './ui';
import { XCircle, AlertCircle } from 'lucide-react';
export const ProblemSection = () => {
  const dolores = [{
    emoji: '😴',
    title: 'El cansancio que no descansa',
    desc: 'Dormiste. Pero te despiertas igual de agotada. Y no importa cuánto descanses, la fatiga no cede.'
  }, {
    emoji: '🌫️',
    title: 'La neblina mental',
    desc: 'Olvidas palabras. Pierdes el hilo. Sientes que ya no eres tan tú. Y eso asusta más de lo que admites.'
  }, {
    emoji: '⚖️',
    title: 'El peso que apareció solo',
    desc: 'No comiste diferente. Pero el abdomen creció. Y la ropa que antes usabas ya no cierra igual.'
  }, {
    emoji: '🌙',
    title: 'Las noches rotas',
    desc: 'Sudores que te despiertan. Corazón acelerado a las 3am. Sin sueño profundo, todo lo demás colapsa.'
  }, {
    emoji: '💔',
    title: 'Las emociones sin aviso',
    desc: 'Irritabilidad. Tristeza sin causa. Ansiedad nueva. Te preguntas si estás perdiendo la cabeza. No la estás perdiendo.'
  }, {
    emoji: '🤫',
    title: 'La soledad de no ser entendida',
    desc: 'Tu pareja no lo entiende. Tus hijos tampoco. Y tus amigas lo niegan. Necesitas saber que no estás sola en esto.'
  }];
  const traditionalFails = [{
    attempt: 'Dormir más horas',
    why: 'No resuelve la calidad del sueño hormonal'
  }, {
    attempt: 'Tomar más café',
    why: 'Agota más tus glándulas suprarrenales'
  }, {
    attempt: 'Dietas restrictivas',
    why: 'Generan más estrés hormonal y más inflamación'
  }, {
    attempt: 'Suplementos genéricos',
    why: 'No están sincronizados con tu biología actual'
  }, {
    attempt: 'Ejercicio intenso',
    why: 'Puede aumentar el cortisol y empeorar el agotamiento'
  }];
  return <>
      {/* Los dolores que nadie nombra */}
      <Section bg="bg-cream-light" data-id="element-253">
        <FadeIn data-id="element-254">
          <div className="max-w-5xl mx-auto" data-id="element-255">
            <div className="text-center mb-12 max-w-3xl mx-auto" data-id="element-256">
              <span className="inline-flex items-center gap-2 text-coral font-bold tracking-wider uppercase text-sm mb-4" data-id="element-257">
                Reconócete aquí
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight" data-id="element-258">
                Los dolores que nadie nombra{' '}
                <span className="text-gold italic font-normal" data-id="element-259">
                  pero todas sienten
                </span>
              </h2>
              <p className="text-brown-light text-lg" data-id="element-260">
                Si alguno de estos te suena familiar, este plan fue escrito para
                ti.
              </p>
            </div>

            <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-261">
              {dolores.map((dolor, i) => <StaggerItem key={i} data-id="element-262">
                  <div className="bg-white p-7 rounded-3xl shadow-sm border border-cream-dark h-full" data-id="element-263">
                    <div className="text-4xl mb-4" aria-hidden="true" data-id="element-264">
                      {dolor.emoji}
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-brown leading-snug" data-id="element-265">
                      {dolor.title}
                    </h3>
                    <p className="text-brown-light text-sm leading-relaxed" data-id="element-266">
                      {dolor.desc}
                    </p>
                  </div>
                </StaggerItem>)}
            </StaggerContainer>

            <div className="mt-12 text-center max-w-2xl mx-auto" data-id="element-267">
              <p className="text-xl md:text-2xl text-brown font-medium leading-relaxed" data-id="element-268">
                Tu cuerpo no te abandonó. Solo cambió las reglas.
              </p>
              <p className="text-lg md:text-xl text-gold font-bold italic mt-3" data-id="element-269">
                Y yo te doy el nuevo manual.
              </p>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* Why Traditional Solutions Fail */}
      <Section bg="bg-white" data-id="element-270">
        <FadeIn data-id="element-271">
          <div className="max-w-4xl mx-auto" data-id="element-272">
            <div className="text-center mb-12" data-id="element-273">
              <span className="inline-flex items-center gap-2 text-coral font-bold tracking-wider uppercase text-sm mb-4" data-id="element-274">
                <AlertCircle className="w-4 h-4" data-id="element-275" />
                Por qué no ha funcionado antes
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-276">
                El problema no eres tú
              </h2>
              <p className="text-brown-light max-w-2xl mx-auto" data-id="element-277">
                Los consejos tradicionales fallan porque están diseñados para un
                cuerpo diferente al tuyo.
              </p>
            </div>

            <div className="bg-cream-light rounded-3xl overflow-hidden border border-cream-dark" data-id="element-278">
              <div className="grid grid-cols-12 bg-brown text-cream-light px-6 py-4 text-sm font-bold uppercase tracking-wider" data-id="element-279">
                <div className="col-span-5" data-id="element-280">Lo que te dijeron que hicieras</div>
                <div className="col-span-7" data-id="element-281">Por qué no funciona para ti</div>
              </div>
              {traditionalFails.map((row, i) => <div key={i} className={`grid grid-cols-12 px-6 py-5 items-start gap-4 ${i !== traditionalFails.length - 1 ? 'border-b border-cream-dark' : ''}`} data-id="element-282">
                  <div className="col-span-5 flex items-start gap-3 font-bold text-brown" data-id="element-283">
                    <XCircle className="w-5 h-5 text-coral mt-0.5 shrink-0" data-id="element-284" />
                    <span data-id="element-285">{row.attempt}</span>
                  </div>
                  <div className="col-span-7 text-brown-light" data-id="element-286">{row.why}</div>
                </div>)}
            </div>

            <div className="mt-12 text-center max-w-2xl mx-auto" data-id="element-287">
              <p className="text-xl md:text-2xl text-brown font-medium leading-relaxed" data-id="element-288">
                El cuerpo de una mujer en menopausia necesita un enfoque
                diferente.
              </p>
              <p className="text-lg text-gold font-bold italic mt-4" data-id="element-289">
                No más esfuerzo. Esfuerzo sincronizado.
              </p>
            </div>
          </div>
        </FadeIn>
      </Section>
    </>;
};